import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, Button } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

/**
 * Persistent banner shown to contractors and verifiers who have not set their
 * Province + District. Those fields are the canonical location that drives
 * marketplace matching, and the backend now blocks bid/proposal submission
 * until they are filled (see middleware/locationGate.js). This banner makes the
 * requirement visible and links straight to the profile so the user can fix it.
 *
 * It is intentionally NOT dismissible — the gate stays until the location is
 * provided. It self-fetches and renders nothing for any role that isn't gated
 * or that already has a location, so it is safe to drop into any dashboard.
 */
const GATED_ROLES = ['contractor', 'verifier'];

const LocationGateBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [missing, setMissing] = useState(false);

  const isGatedRole = GATED_ROLES.includes(user?.role);

  useEffect(() => {
    let active = true;
    if (!isGatedRole) return undefined;

    api.get('/auth/users/profile')
      .then((res) => {
        if (!active) return;
        const p = res.data?.data || {};
        const hasLocation = Boolean(
          (p.province && String(p.province).trim()) &&
          (p.district && String(p.district).trim())
        );
        setMissing(!hasLocation);
      })
      .catch(() => {
        // Fail closed-silent: if we can't determine location, don't nag.
        if (active) setMissing(false);
      });

    return () => { active = false; };
  }, [isGatedRole, user?.id]);

  if (!isGatedRole || !missing) return null;

  return (
    <Alert
      severity="warning"
      icon={<LocationOnIcon />}
      sx={{ mb: 2, alignItems: 'center' }}
      action={(
        <Button
          color="inherit"
          size="small"
          variant="outlined"
          onClick={() => navigate('/profile')}
        >
          Add location
        </Button>
      )}
    >
      <AlertTitle sx={{ mb: 0.25 }}>Add your location to receive work</AlertTitle>
      Set your <strong>Province</strong> and <strong>District</strong> in your profile. Until then,
      you can&rsquo;t submit bids or proposals and won&rsquo;t be matched with work in your area.
    </Alert>
  );
};

export default LocationGateBanner;
